// Layer 3 (Transformation) — the shared downtime impact band.
// Pure projections from the event log, planning calendar and capacity engine.

import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import type { RecoveryState } from '@/versions/v1/schema/capacity';
import type { MetalPriceState } from '@/versions/v1/schema/capacity';
import { addMinutes, formatDayMonth } from '@/versions/v1/shared/clock.derive';
import { capacityAtReference } from '@/versions/v1/shared/CapacityEngine';
import { CAPACITY_SEED } from '@/versions/v1/shared/capacity-seed';
import { ANNUAL_PLAN_BUDGETS, PLANNING_MONTH_PROFILE, PLANNING_PERIODS, yearOneScheduledHours } from '@/versions/v1/shared/bunker-hill-planning-seed';
import { BASIS_END } from '../maintenance/downtime.seed';
import type { DowntimeEvent } from '../maintenance/DowntimeEvents.transform';
import { ebitdaPerOperatingHour, valueLostByCategory, valueLostForEvent, valueLostHoursForEvent, valueLostTonnesForEvent } from './ValueLost.transform';

export type DowntimeWindowId = 'ytd' | 'month' | '7d';

export type DowntimeWindow = {
  id: DowntimeWindowId;
  label: string;
  from: SeriesTime;
  to: SeriesTime;
  rangeLabel: string;
};

const MONTH_NUMBERS: Readonly<Record<string, number>> = {
  Jan: 1, Feb: 2, Mar: 3, Apr: 4, May: 5, Jun: 6,
  Jul: 7, Aug: 8, Sep: 9, Oct: 10, Nov: 11, Dec: 12,
};

function monthStart(time: SeriesTime): SeriesTime {
  return `${time.slice(0, 7)}-01T00:00` as SeriesTime;
}

function previousMonthStart(time: SeriesTime): SeriesTime {
  const [year, month] = time.slice(0, 7).split('-').map(Number);
  return new Date(Date.UTC(year!, month! - 2, 1)).toISOString().slice(0, 16) as SeriesTime;
}

function monthName(time: SeriesTime): string {
  return new Date(`${time}Z`).toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

function profileMonthStart(month: (typeof PLANNING_MONTH_PROFILE)[number]): SeriesTime {
  return `${month.year}-${String(MONTH_NUMBERS[month.month]).padStart(2, '0')}-01T00:00` as SeriesTime;
}

function nextProfileMonthStart(month: (typeof PLANNING_MONTH_PROFILE)[number]): SeriesTime {
  return new Date(Date.UTC(month.year, MONTH_NUMBERS[month.month], 1)).toISOString().slice(0, 16) as SeriesTime;
}

/** Operating hours from the Year 1 profile inside a calendar-time window.
 *  The monthly shutdown allowance is prorated for partial months. */
export function planningScheduledHours(from: SeriesTime, to: SeriesTime): number {
  const startMs = Date.parse(`${from}Z`);
  const endMs = Date.parse(`${to}Z`);
  return PLANNING_MONTH_PROFILE.reduce((total, month) => {
    const monthStartMs = Date.parse(`${profileMonthStart(month)}Z`);
    const monthEndMs = Date.parse(`${nextProfileMonthStart(month)}Z`);
    const overlapHours = Math.max(0, Math.min(endMs, monthEndMs) - Math.max(startMs, monthStartMs)) / 3_600_000;
    return total + overlapHours * (month.calendarDays - month.plannedShutdownDays) / month.calendarDays;
  }, 0);
}

const priorMonthEnd = monthStart(BASIS_END);
const priorMonthFrom = previousMonthStart(BASIS_END);
const lastSevenFrom = addMinutes(BASIS_END, -7 * 24 * 60);
const YEAR_ONE_START = `${PLANNING_PERIODS.find((period) => period.id === 'pp-y1')!.startDate}T00:00` as SeriesTime;

export const DOWNTIME_WINDOWS: readonly DowntimeWindow[] = [
  {
    id: 'ytd',
    label: 'Year 1 to date',
    from: YEAR_ONE_START,
    to: BASIS_END,
    rangeLabel: `${formatDayMonth(YEAR_ONE_START)} – ${formatDayMonth(addMinutes(BASIS_END, -24 * 60))}`,
  },
  {
    id: 'month',
    label: monthName(priorMonthFrom),
    from: priorMonthFrom,
    to: priorMonthEnd,
    rangeLabel: `${formatDayMonth(priorMonthFrom)} – ${formatDayMonth(addMinutes(priorMonthEnd, -24 * 60))}`,
  },
  {
    id: '7d',
    label: 'Last 7 days',
    from: lastSevenFrom,
    to: BASIS_END,
    rangeLabel: `${formatDayMonth(lastSevenFrom)} – ${formatDayMonth(addMinutes(BASIS_END, -24 * 60))}`,
  },
];

export function isDowntimeWindowId(value: string | null): value is DowntimeWindowId {
  return value === 'ytd' || value === 'month' || value === '7d';
}

export function downtimeWindow(windowId: DowntimeWindowId): DowntimeWindow {
  return DOWNTIME_WINDOWS.find((window) => window.id === windowId) ?? DOWNTIME_WINDOWS[0]!;
}

export function downtimeWindowRows(
  rows: readonly DowntimeEvent[],
  windowId: DowntimeWindowId,
): readonly DowntimeEvent[] {
  const window = downtimeWindow(windowId);
  return rows.filter((event) => event.start >= window.from && event.end <= window.to);
}

export type MonthlyMillAvailabilityRead = {
  id: string;
  label: string;
  outageHours: number;
  scheduledHours: number;
  availabilityPct: number;
  annualTargetPct: number;
};

/** Mill availability from the stops that take the mill off production, read
 *  against the planning schedule. Other equipment hours remain in the log. */
export function downtimeMillAvailabilityByMonth(
  allRows: readonly DowntimeEvent[],
): MonthlyMillAvailabilityRead[] {
  const currentMonthKey = BASIS_END.slice(0, 7);
  return PLANNING_MONTH_PROFILE
    .filter((month) => profileMonthStart(month) < BASIS_END)
    .map((month) => {
      const from = profileMonthStart(month);
      const to = `${month.year}-${String(MONTH_NUMBERS[month.month]).padStart(2, '0')}` === currentMonthKey
        ? BASIS_END
        : nextProfileMonthStart(month);
      const rows = allRows.filter((event) => event.kind === 'unplanned' && event.start >= from && event.end <= to);
      const outageHours = rows.reduce((total, event) => total + valueLostHoursForEvent(event), 0);
      const scheduledHours = planningScheduledHours(from, to);
      return {
        id: month.id,
        label: month.label,
        outageHours,
        scheduledHours,
        availabilityPct: scheduledHours > 0 ? ((scheduledHours - outageHours) / scheduledHours) * 100 : 0,
        annualTargetPct: ANNUAL_PLAN_BUDGETS.millAvailabilityPct,
      };
    });
}

export type DowntimeImpactRead = {
  window: DowntimeWindow;
  outageHours: number;
  /** Net mill-production loss after upstream inventory buffer. */
  lostHours: number;
  lostTonnes: number;
  valueLostA: number;
  planRateTonnesPerHour: number;
  scheduledHours: number;
  availabilityPct: number;
  monthlyAvailability: MonthlyMillAvailabilityRead[];
  plannedTonnes: number;
  lostPctOfPlan: number;
  periodEbitdaA: number;
  lostPctOfEbitda: number;
  causeRows: ReturnType<typeof valueLostByCategory>;
  topCauses: ReturnType<typeof valueLostByCategory>;
};

export function downtimeImpactRead(
  allRows: readonly DowntimeEvent[],
  windowId: DowntimeWindowId,
): DowntimeImpactRead {
  const window = downtimeWindow(windowId);
  const rows = downtimeWindowRows(allRows, windowId);
  const unplanned = rows.filter((event) => event.kind === 'unplanned');
  const lostHours = unplanned.reduce((total, event) => total + valueLostHoursForEvent(event), 0);
  const lostTonnes = unplanned.reduce((total, event) => total + valueLostTonnesForEvent(event), 0);
  const valueLostA = unplanned.reduce((total, event) => total + valueLostForEvent(event), 0);
  const scheduledHours = planningScheduledHours(window.from, window.to);
  const capacity = capacityAtReference(CAPACITY_SEED);
  const plannedTonnes = scheduledHours * capacity.currentThroughputStph;
  const periodEbitdaA = ebitdaPerOperatingHour() * scheduledHours;
  const causeRows = valueLostByCategory(rows);

  return {
    window,
    outageHours: lostHours,
    lostHours,
    lostTonnes,
    valueLostA,
    planRateTonnesPerHour: capacity.currentThroughputStph,
    scheduledHours,
    availabilityPct: scheduledHours > 0 ? ((scheduledHours - lostHours) / scheduledHours) * 100 : 0,
    monthlyAvailability: downtimeMillAvailabilityByMonth(allRows),
    plannedTonnes,
    lostPctOfPlan: plannedTonnes > 0 ? (lostTonnes / plannedTonnes) * 100 : 0,
    periodEbitdaA,
    lostPctOfEbitda: periodEbitdaA > 0 ? (valueLostA / periodEbitdaA) * 100 : 0,
    causeRows,
    topCauses: causeRows.filter((row) => row.dollars > 0).slice(0, 3),
  };
}

export type UptimeOpportunityRead = {
  availabilityLiftPctPoints: number;
  yearOneScheduledHours: number;
  additionalHours: number;
  throughputStph: number;
  additionalFeedShortTons: number;
  currentEbitdaPerHourA: number;
  annualEbitdaA: number;
  grade: typeof CAPACITY_SEED.grade;
  prices: MetalPriceState;
  recoveries: RecoveryState;
};

export function uptimeOpportunityRead(points = 1): UptimeOpportunityRead {
  const scheduledHours = yearOneScheduledHours();
  const additionalHours = scheduledHours * (points / 100);
  const capacity = capacityAtReference(CAPACITY_SEED);
  const current = capacity.scenarios.find((scenario) => scenario.id === 'current')!;
  const recoveries = current.recoveries;
  const additionalFeedShortTons = additionalHours * capacity.currentThroughputStph;
  const currentEbitdaPerHourA = ebitdaPerOperatingHour();

  return {
    availabilityLiftPctPoints: points,
    yearOneScheduledHours: scheduledHours,
    additionalHours,
    throughputStph: capacity.currentThroughputStph,
    additionalFeedShortTons,
    currentEbitdaPerHourA,
    annualEbitdaA: currentEbitdaPerHourA * additionalHours,
    grade: capacity.grade,
    prices: capacity.prices,
    recoveries,
  };
}

export type AvailabilityBandMonth = {
  id: string;
  /** 'M1' — the Year 1 month code. */
  code: string;
  /** 'Mar 2026'. */
  monthLabel: string;
  availabilityPct: number;
  atPlan: boolean;
};

/** The monthly mill-availability chart with one availability point priced on
 *  its axis (D12): the band runs from the Year 1 plan to one point above it,
 *  and carries the hours and US$/yr `uptimeOpportunityRead` prices that point
 *  at. Nothing here is typed; the axis bounds follow the data. */
export type AvailabilityBandRead = {
  months: AvailabilityBandMonth[];
  planPct: number;
  bandLowPct: number;
  bandHighPct: number;
  /** The band's height in availability points. */
  bandPts: number;
  bandHours: number;
  bandAnnualA: number;
  latest: AvailabilityBandMonth;
  /** Latest month's availability less the plan, in percentage points. */
  latestVsPlanPts: number;
  domain: readonly [number, number];
  ticks: number[];
};

export function millAvailabilityBand(
  monthly: readonly MonthlyMillAvailabilityRead[],
  uptime: UptimeOpportunityRead = uptimeOpportunityRead(),
): AvailabilityBandRead {
  const planPct = monthly[0]?.annualTargetPct ?? ANNUAL_PLAN_BUDGETS.millAvailabilityPct;
  const months = monthly.map((month) => {
    const [code, ...rest] = month.label.split(' ');
    return {
      id: month.id,
      code: code ?? month.id.toUpperCase(),
      monthLabel: rest.join(' '),
      availabilityPct: month.availabilityPct,
      atPlan: month.availabilityPct >= planPct,
    };
  });
  const bandHighPct = planPct + uptime.availabilityLiftPctPoints;
  const values = months.map((month) => month.availabilityPct);
  const low = Math.floor(Math.min(planPct, ...values)) - 1;
  const high = Math.ceil(Math.max(bandHighPct, ...values));
  const ticks: number[] = [];
  for (let tick = Math.ceil(low / 2) * 2; tick <= high; tick += 2) ticks.push(tick);
  const latest = months.at(-1)!;
  return {
    months,
    planPct,
    bandLowPct: planPct,
    bandHighPct,
    bandPts: uptime.availabilityLiftPctPoints,
    bandHours: uptime.additionalHours,
    bandAnnualA: uptime.annualEbitdaA,
    latest,
    latestVsPlanPts: latest.availabilityPct - planPct,
    domain: [low, high],
    ticks,
  };
}
